import React from 'react';
import {
  Typography,
  Row as GridRow,
  Col,
} from 'design-system';
import {
  AlertFilledIcon,
  ArrowLeftIcon,
  OpenLinkIcon,
  SearchIcon,
} from '../../../design-system/components/Icons';
import { DS_ICONS_SHOWCASE, ICON_SIZE_SCALE, ICON_COLOR_VARIANTS } from './iconsShowcaseData';

const Separator = ({ className = '' }) => (
  <div className={['separator', className].filter(Boolean).join(' ')} />
);

export default function IconsShowcase() {
  return (
    <>
        {/* Icons Section */}
        <GridRow>
          <Col xs={12}>
            <div className="design-system-section">
              <Typography variant="h1" className="section-title mb-4">
                Icons
              </Typography>
              <Separator className="mb-5" />

              {/* Description */}
              <Typography variant="bodyMedium" color="muted" className="mb-2">
                All icons use <code>currentColor</code> — set color via CSS <code>color</code> property or{' '}
                <code>ds-icon--color-*</code> utility classes. Default size is <code>20px</code>.
              </Typography>
              <Typography variant="bodyMedium" color="muted" className="mb-5">
                Import from <code>design-system/components/Icons</code>. Pass <code>aria-label</code> when the icon
                conveys meaning without adjacent visible text.
              </Typography>

              {/* Size scale */}
              <Typography variant="headingSmall" className="mb-3">
                Size scale
              </Typography>
              <div className="ds-icons-showcase__scale-row mb-5">
                {ICON_SIZE_SCALE.map(({ label, size }) => (
                  <div key={label} className="ds-icons-showcase__scale-cell">
                    <SearchIcon size={size} />
                    <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                      {label} · {size}px
                    </Typography>
                  </div>
                ))}
              </div>

              {/* Color variants */}
              <Typography variant="headingSmall" className="mb-3">
                Color variants — via <code>ds-icon--color-*</code>
              </Typography>
              <div className="ds-icons-showcase__color-row mb-5">
                {ICON_COLOR_VARIANTS.map(({ label, className }) => (
                  <div key={label} className="ds-icons-showcase__color-cell">
                    <AlertFilledIcon size={24} fill="currentColor" className={className} />
                    <Typography variant="bodySmall" color="muted">
                      {label}
                    </Typography>
                  </div>
                ))}
              </div>

              <Typography variant="headingSmall" className="mb-3">
                OpenLinkIcon — size, color, stroke
              </Typography>
              <div className="ds-icons-showcase__scale-row mb-5">
                <div className="ds-icons-showcase__scale-cell">
                  <OpenLinkIcon size={12} />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    12px · theme default
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <OpenLinkIcon size={15} stroke="currentColor" className="ds-icon--color-primary" />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    15px · primary utility
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <OpenLinkIcon size={18} stroke="var(--pal-grey-slate-700)" strokeWidth={1.25} />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    18px · slate
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <OpenLinkIcon size={20} color="#8F63AA" strokeWidth={1.5} />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    20px · custom color
                  </Typography>
                </div>
              </div>

              <Typography variant="headingSmall" className="mb-3">
                ArrowLeftIcon — size, color, stroke, style
              </Typography>
              <div className="ds-icons-showcase__scale-row mb-5">
                <div className="ds-icons-showcase__scale-cell">
                  <ArrowLeftIcon size={12} stroke="var(--ds-theme-action-primary)" />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    12px · primary
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <ArrowLeftIcon size={16} stroke="var(--pal-grey-slate-900)" strokeWidth={1.5} />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    16px · slate
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <ArrowLeftIcon size={20} className="ds-icon--color-success" />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    20px · success
                  </Typography>
                </div>
                <div className="ds-icons-showcase__scale-cell">
                  <ArrowLeftIcon size={24} color="#0D101C" strokeWidth={2} style={{ opacity: 0.85 }} />
                  <Typography variant="bodySmall" color="muted" className="ds-icons-showcase__scale-label">
                    24px · custom style
                  </Typography>
                </div>
              </div>

              {/* All icons grid */}
              <Typography variant="headingSmall" className="mb-3">
                All icons · {DS_ICONS_SHOWCASE.length} total
              </Typography>
              <div className="ds-icons-showcase">
                {DS_ICONS_SHOWCASE.map(({ name, Component }) => (
                  <button
                    key={name}
                    type="button"
                    className="ds-icons-showcase__tile"
                    title={`Click to copy: <${name} />`}
                    onClick={() => {
                      navigator.clipboard?.writeText(`<${name} />`);
                    }}
                  >
                    <div className="ds-icons-showcase__glyph">
                      <Component size={20} />
                    </div>
                    <Typography variant="bodyExtraSmall" className="ds-icons-showcase__name">
                      {name}
                    </Typography>
                  </button>
                ))}
              </div>
            </div>
          </Col>
        </GridRow>
    </>
  );
}